import { GasType, PsychrometerProperties } from './CalculatorConditions';
/*
 * Copyright (c) Vaisala Oyj. All rights reserved.
 */

// Constants
const MH2O = 18.0154; // Molar mass of water g/mol
const Cpw = 1.84; // Specific heat capacity of water vapor at constant pressure at 0 °C temperature kJ/kg°C
const hwe = 2501; // Evaporation heat of water in 0 °C kJ/kg
const R = 8.3145; // J*K^-1*mol^-1

export const eis = (t: number) => {
  // t=temperature in °C. Saturation vapor pressure in hPa without enhancement factor
  const tKelv = t + 273.15;
  const A = [1, -6.0245282e3, 2.932707e1, 1.0613868e-2, -1.3198825e-5, -4.9382577e-1, 1];

  const eis = (A[0] * Math.exp(A[1] / tKelv + A[2] + A[3] * tKelv + A[4] * tKelv ** 2 + A[5] * Math.log(A[6] * tKelv))) / 100;

  return eis;
};

export const ews = (t: number) => {
  // t=temperature in °C. Saturation vapor pressure in hPa without enhancement factor
  const tKelv = t + 273.15;
  const A = [1, -6.0969385e3, 2.12409642e1, -2.711193e-2, 1.673952e-5, 2.433502, 1];

  const ews = (A[0] * Math.exp(A[1] / tKelv + A[2] + A[3] * tKelv + A[4] * tKelv ** 2 + A[5] * Math.log(A[6] * tKelv))) / 100;

  return ews;
};

export const enhancementFactor = (t: number, p: number, gasType: GasType) => {
  // p= Total pressure in HPa, t = temperature in °C.
  // For ambient pressure and lower pressures this effect is typically
  // ignored. But at pressures significantly above ambient it has to be taken
  // into account.For CO2-free air the following equation for f has been
  // proposed (Greenspan: J. of Research of the NBS vol 80A, No. 1 p 41-44)

  // Enhancement factor applies only if Air or N2 is chosen
  if (!['gas.Air', 'gas.N2'].includes(gasType)) return 1;

  let A = [];
  let B = [];
  let saturationPressure;

  // The parameters Ai and Bi: ice
  if (t >= -100 && t < 0) {
    saturationPressure = eis(t);
    A = [3.64449e-4, 2.9367585e-5, 4.8874766e-7, 4.3669918e-9];
    B = [-10.7271, 7.6215115e-2, -1.7490155e-4, 2.4668279e-6];
  }
  // The parameters Ai and Bi: water
  else if (t >= 0 && t <= 100) {
    saturationPressure = ews(t);
    A = [3.53624e-4, 2.9328363e-5, 2.6168979e-7, 8.5813609e-9];
    B = [-1.07588e1, 6.3268134e-2, -2.5368934e-4, 6.3405286e-7];
  } else {
    return 1;
  }
  // Out of specs if pressure is higher than 2 MPa == 20000 hPa
  if (p > 20000) {
    // console.warn('Pressure out of specs');
    return 1;
  }

  const alpha = A[0] + A[1] * t + A[2] * t ** 2 + A[3] * t ** 3;
  const beta = Math.exp(B[0] + B[1] * t + B[2] * t ** 2 + B[3] * t ** 3);

  let f = Math.exp(alpha * (1 - saturationPressure / p) + beta * (p / saturationPressure - 1));

  if (f < 1) {
    f = 1;
  }
  return f;
};

// t=temperature in °C, p=pressure in hPa
// returns water saturation pressure in hPa. Compensated with enchancement factor
export const pws = (t: number, p: number, gasType: GasType) => {
  // console.log(ews(t), enhancementFactor(t, p, gasType));
  return ews(t) * enhancementFactor(t, p, gasType);
};

// t=temperature in °C, p=pressure in hPa
// returns gas density
export const gasDensity = (t: number, p: number, Mgas: number, pw: number) => {
  const ph2o = (pw * MH2O) / 10 / R / (t + 273.15);
  const pgas = (((p - pw) / 10) * Mgas) / R / (t + 273.15);
  return ph2o + pgas;
};

// returns gas Specific volume
export const specificVolume = (t: number, p: number, Mgas: number, pw: number) => {
  const ph2o = (pw * MH2O) / 10 / R / (t + 273.15);
  const pgas = (((p - pw) / 10) * Mgas) / R / (t + 273.15);
  return 1 / (ph2o + pgas);
};

// t=temperature in °C, p=pressure in hPa
// returns water saturation pressure (over ice) in hPa. Compensated with enchancement factor
export const pis = (t: number, p: number, gasType: GasType) => eis(t) * enhancementFactor(t, p, gasType);

// Calculation functions from water vapor pressure to other humidity parameters
// ****************************************************************************

// Relative humidity is at all temperatures and pressures defined as the ratio of the water vapour pressure to the saturation water vapour pressure (over water) at the gas temperature
export const pwToRh = (pw: number, pws: number) => (pw / pws) * 100;

// Relative humidity (Frost) is at temperatures below 0 and all pressures defined as the ratio of the water vapour pressure to the saturation water vapour pressure (over ice) at the gas temperature
export const pwToRhi = (pw: number, pis: number) => (pw / pis) * 100;

export const pwToTd = (pw: number, t: number, p: number, gasType: GasType) => {
  const SaturationPressure = pws(t, p, gasType);
  const rh = pwToRh(pw, SaturationPressure);

  let tdMax = t + 5;
  let tdMin = -100;

  while (tdMax - tdMin > 0.000001) {
    const tdAverage = (tdMin + tdMax) / 2;
    const pwAverage = pws(tdAverage, p, gasType);
    let rhAverage = pwToRh(pwAverage, SaturationPressure);

    if (rhAverage >= rh) {
      tdMax = tdAverage;
    } else {
      tdMin = tdAverage;
    }
  }

  return (tdMin + tdMax) / 2;
};

export const pwToTdf = (pw: number, t: number, p: number, gasType: GasType) => {
  // Use this function to calculate Tdf only if pwToTd returns a negative value (< 0 DegC)

  const SaturationPressure = pis(t, p, gasType);
  const rhi = pwToRhi(pw, SaturationPressure);

  let tdfMax = 0;
  let tdfMin = -100;

  while (tdfMax - tdfMin > 0.000001) {
    const tdfAverage = (tdfMin + tdfMax) / 2;
    const pwAverage = pis(tdfAverage, p, gasType);
    let rhiAverage = pwToRhi(pwAverage, SaturationPressure);

    if (rhiAverage >= rhi) {
      tdfMax = tdfAverage;
    } else {
      tdfMin = tdfAverage;
    }
  }

  return (tdfMin + tdfMax) / 2;
};

export const pwToTw = (pw: number, t: number, p: number, K: PsychrometerProperties, gasType: GasType) => {
  const SaturationPressure = pws(t, p, gasType);
  const rh = pwToRh(pw, SaturationPressure);

  let twMax = t + 5;
  let twMin = -100;

  while (twMax - twMin > 0.000001) {
    const twAverage = (twMin + twMax) / 2;
    const pwAverage = twToPw(twAverage, t, p, K, gasType);
    let rhAverage = pwToRh(pwAverage, SaturationPressure);

    if (rhAverage >= rh) {
      twMax = twAverage;
    } else {
      twMin = twAverage;
    }
  }

  return (twMin + twMax) / 2;
};

// Calculate Absolute humidity from vapor pressure
export const pwToA = (pw: number, t: number) => {
  // const R = 8.3145; // J*K^-1*mol^-1

  return (MH2O * pw * 100) / (R * (t + 273.15));
};
// Calculate Mixing ratio from vapor pressure
export const pwToX = (pw: number, p: number, Mgas: number) => {
  const X = 1000 * (MH2O / Mgas) * (pw / (p - pw));
  if (X > 1000000 || X < 0) return NaN;
  else return X;
};
// Calculate enthalpy
export const pwToH = (pw: number, t: number, tRef: number, p: number, Mgas: number, Cpg: number) =>
  Cpg * (t - tRef) + (pwToX(pw, p, Mgas) / 1000) * (Cpw * (t - tRef) + hwe);

// Parts Per Million ppmv(dry) volume/volume
// In dry conditions, parts per million values are usually given vs. the amount of dry air'''
export const pwToPpmv = (pw: number, p: number) => {
  const PPMv = (pw / (p - pw)) * 1e6;
  if (PPMv > 1000000 || PPMv < 0) return NaN;
  else return PPMv;
};

// Parts Per Million ppmv(wet) volume/volume
export const pwToPpmvwet = (pw: number, p: number) => (pw / p) * 1e6;

// Calculation functions from other humidity parameters into water vapor pressure
// ******************************************************************************

// Calculate vapor pressure from RH and saturation vapor pressure
// export const rhToPw = (rh: number, pws: number) => (rh / 100) * pws;
export const rhToPw = (rh: number, saturationPressure: number) => (rh / 100) * saturationPressure;

// Calculate vapor pressure from RHi and saturation vapor pressure
export const rhiToPw = (rhi: number, pis: number) => (rhi / 100) * pis;

// Calculate vapor pressure from Dew point temperature
export const tdToPw = (td: number, p: number, gasType: GasType) => pws(td, p, gasType);
// Calculate vapor pressure from Frost point temperature
export const tdfToPw = (tdf: number, p: number, gasType: GasType) => {
  if (tdf >= 0) {
    return pws(tdf, p, gasType);
  } else {
    return pis(tdf, p, gasType);
  }
};
// Calculate vapor pressure from wet bulb temperature
export const twToPw = (tw: number, t: number, p: number, K: PsychrometerProperties, gasType: GasType) => {
  const result = pws(tw, p, gasType) - p * K.psychrometerConstantA * (t - tw) * (1 + K.psychrometerConstantB * tw);
  return result > 0 ? result : 0;
};

// Calculate vapor pressure from Absolute humidity
export const aToPw = (a: number, t: number) => {
  // const R = 8.3145; // J*K^-1*mol^-1

  return (a * (R * (t + 273.15))) / (MH2O * 100);
};

export const xToPw = (x: number, p: number, Mgas: number) => (x * p) / ((MH2O / Mgas) * 1000 + x);

// Enthalpy calculation. tRef is reference temperature. In SI-units 0 °C. In imperial units 0 °F = -17.778 °C
export const hToPw = (h: number, t: number, tRef: number, p: number, Mgas: number, Cpg: number) => {
  const x = (1000 * (h - Cpg * (t - tRef))) / (Cpw * (t - tRef) + hwe); // g/kg

  return (x * p) / ((MH2O / Mgas) * 1000 + x);
};

export const ppmvToPw = (ppmv: number, p: number) => (ppmv * p) / (1e6 + ppmv);

export const ppmvwetToPw = (ppmvwet: number, p: number) => (ppmvwet * p) / 1e6;
